import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { PdfLabeledSections } from './shared/PdfLabeledSections';
import { CartLinesPdfTable } from './shared/CartLinesPdfTable';
import { CompanyPdfHeader } from './shared/CompanyPdfHeader';
import { formatPdfEur } from './shared/formatPdfCurrency';
import { LabeledPdfRows } from './shared/LabeledPdfBlock';
import { PdfPrintScaleProvider, usePdfStyles } from './shared/PdfPrintScaleContext';

function CreditNotePdfPage({ data }) {
    const pdfStyles = usePdfStyles();
    const subtitleParts = [`Credit note #${data.creditNoteNumber || '—'}`];
    subtitleParts.push(`Order #${data.orderNumber || '—'}`);
    if (data.invoiceNumber) {
        subtitleParts.push(`Invoice #${data.invoiceNumber}`);
    }
    const subtitle = subtitleParts.join(' · ');

    const summaryRows = [
        { label: 'Credit note #', value: data.creditNoteNumber },
        { label: 'Credit note date', value: data.creditNoteDate },
        { label: 'Order status', value: data.status },
        { label: 'Order date', value: data.orderDate },
    ];
    if (data.credit?.paymentStatus) {
        summaryRows.push({ label: 'Credit payment', value: data.credit.paymentStatus });
    }

    const creditRows = [
        { label: 'Credit items total', value: formatPdfEur(data.credit?.itemsSubtotal) },
        { label: 'Surcharge (credit)', value: formatPdfEur(data.credit?.surcharge) },
        { label: 'Shipping (credit)', value: formatPdfEur(data.credit?.shipping) },
        { label: 'VAT (18%)', value: formatPdfEur(data.credit?.vat18) },
        { label: 'Combined credit total', value: formatPdfEur(data.credit?.combined) },
    ];
    if (data.credit?.method) {
        creditRows.push({ label: 'Method', value: String(data.credit.method) });
    }

    const billingRows = [
        { label: 'Full name', value: data.billing?.name },
        { label: 'Company', value: data.billing?.company },
        { label: 'VAT no.', value: data.billing?.vat },
        { label: 'Street', value: data.billing?.street },
        { label: 'Town / city', value: data.billing?.city },
        { label: 'Post code', value: data.billing?.postCode },
        { label: 'Phone', value: data.billing?.phone },
        { label: 'Email', value: data.billing?.email },
    ];

    const deliveryRows = [
        { label: 'Full name', value: data.delivery?.name },
        { label: 'Company', value: data.delivery?.company },
        { label: 'VAT no.', value: data.delivery?.vat },
        { label: 'Street', value: data.delivery?.street },
        { label: 'Town / city', value: data.delivery?.city },
        { label: 'Post code', value: data.delivery?.postCode },
        { label: 'Phone', value: data.delivery?.phone },
        { label: 'Email', value: data.delivery?.email },
    ];

    return (
        <Page size="A4" style={pdfStyles.page}>
                <CompanyPdfHeader title="Credit note" subtitle={subtitle} />

                <Text style={pdfStyles.sectionTitle}>Summary</Text>
                <View style={pdfStyles.twoCol}>
                    <View style={pdfStyles.col}>
                        <LabeledPdfRows rows={summaryRows} />
                    </View>
                    <View style={pdfStyles.col} />
                </View>

                {data.note ? (
                    <View style={{ marginTop: 8 }}>
                        <Text style={pdfStyles.sectionTitle}>Order note</Text>
                        <Text style={{ fontSize: 8 }}>{data.note}</Text>
                    </View>
                ) : null}

                <Text style={pdfStyles.sectionTitle}>Credited line items</Text>
                <CartLinesPdfTable lines={data.cartLines || []} />

                <PdfLabeledSections
                    sections={[
                        { title: 'Credit totals', rows: creditRows },
                        { title: 'Billing', rows: billingRows, wrap: false },
                        { title: 'Delivery', rows: deliveryRows, wrap: false },
                    ]}
                />
        </Page>
    );
}

/**
 * Credit note PDF — feed {@link normalizeCreditNoteForPdf} output as `data`.
 * @param {{ data: Record<string, unknown>; scale?: number }} props
 */
export function CreditNotePdfDocument({ data, scale = 1 }) {
    return (
        <Document title={`Credit note ${data.creditNoteNumber || ''}`}>
            <PdfPrintScaleProvider scale={scale}>
                <CreditNotePdfPage data={data} />
            </PdfPrintScaleProvider>
        </Document>
    );
}
